import { surfaceSupportRadius } from '@hyperlight/shared';
/**
 * Resource node rendering + queries. Placement comes from the shared seed
 * function (identical on the server); DEPLETION arrives over the wire and
 * drives the visuals — a vein shrinks and dims as the room mines it, and a
 * dry node is a dark stub until the server refills it.
 *
 * Meshes live in each planet's group (planet-local coordinates), so they
 * ride the floating origin with the terrain they sit on.
 */
import * as THREE from "three";
import {
  systemNodes,
  NODE_KINDS,
  rngFromString,
  type ResourceNode,
  hiddenSystemManifest,
} from "@hyperlight/shared";
import type { StarSystem } from "./starsystem";

interface NodeVis {
  node: ResourceNode;
  group: THREE.Group;
  glow: THREE.Sprite;
  /** PLANET-LOCAL position (the planet rotates and orbits). */
  local: THREE.Vector3;
  planet: import("./planet").Planet;
  left: number;
}

let glowTex: THREE.CanvasTexture | null = null;
function getGlowTex(): THREE.CanvasTexture {
  if (glowTex) return glowTex;
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, "rgba(255,255,255,0.9)");
  g.addColorStop(0.4, "rgba(255,255,255,0.25)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  glowTex = new THREE.CanvasTexture(canvas);
  return glowTex;
}

export class ResourceNodes {
  private vis = new Map<number, NodeVis>();
  private t = 0;

  constructor(system: StarSystem) {
    const nodes = systemNodes(system.def.planets, hiddenSystemManifest(system.def.id));
    const up = new THREE.Vector3();
    for (const node of nodes.values()) {
      const planet = system.planets[node.planet];
      if (!planet) continue;
      const kind = NODE_KINDS[node.kind];
      const rng = rngFromString(`${planet.def.seed}:nodevis:${node.id}`);
      up.set(node.dir[0], node.dir[1], node.dir[2]);
      const r = surfaceSupportRadius(planet.sampler, up.x, up.y, up.z);

      const group = new THREE.Group();
      const color = new THREE.Color(kind.color);
      const mat = new THREE.MeshLambertMaterial({
        color,
        emissive: color.clone().multiplyScalar(0.45),
      });
      // A cluster of ore shards jutting from the ground.
      const shards = 4 + Math.floor(rng() * 3);
      for (let i = 0; i < shards; i++) {
        const sr = 0.5 + rng() * 0.9;
        const geo = new THREE.OctahedronGeometry(sr, 0);
        geo.scale(1, 1.6 + rng() * 1.6, 1);
        const m = new THREE.Mesh(geo, mat);
        m.position.set((rng() - 0.5) * 2.6, sr * 0.8, (rng() - 0.5) * 2.6);
        m.rotation.set((rng() - 0.5) * 0.7, rng() * Math.PI, (rng() - 0.5) * 0.7);
        group.add(m);
      }
      // A soft beacon so prospectors can spot veins from the air.
      const glow = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: getGlowTex(),
          color: kind.color,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          transparent: true,
          opacity: 0.55,
        }),
      );
      glow.scale.set(14, 14, 1);
      glow.position.y = 2;
      group.add(glow);

      // Planet-local placement: drape on the terrain, align with local up.
      group.position.copy(up).multiplyScalar(r - 0.2);
      group.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), up);
      planet.group.add(group);

      this.vis.set(node.id, {
        node,
        group,
        glow,
        local: new THREE.Vector3(up.x * r, up.y * r, up.z * r),
        planet,
        left: node.capacity,
      });
    }
  }

  /** Apply a server depletion snapshot/delta. */
  setLeft(list: { id: number; left: number }[]): void {
    for (const { id, left } of list) {
      const v = this.vis.get(id);
      if (!v) continue;
      v.left = left;
      const k = Math.max(0, Math.min(1, left / v.node.capacity));
      const s = 0.3 + 0.7 * k;
      v.group.scale.set(s, s, s);
      (v.glow.material as THREE.SpriteMaterial).opacity = k > 0 ? 0.2 + 0.35 * k : 0.04;
    }
  }

  /** Remaining units in a node (capacity until the server says otherwise). */
  leftOf(id: number): number {
    return this.vis.get(id)?.left ?? 0;
  }

  private worldTmp = new THREE.Vector3();

  /** Live world position of a node (the planet rotates AND orbits). */
  private worldOf(v: NodeVis, out: THREE.Vector3): THREE.Vector3 {
    return out.copy(v.local).applyQuaternion(v.planet.pose.quat).add(v.planet.pose.center);
  }

  /** Nearest LIVE node within `maxR` of a system-space point. */
  nearest(pos: THREE.Vector3, maxR: number): { node: ResourceNode; pos: THREE.Vector3; dist: number } | null {
    let best: NodeVis | null = null;
    let bestD = maxR;
    for (const v of this.vis.values()) {
      if (v.left <= 0) continue;
      const d = this.worldOf(v, this.worldTmp).distanceTo(pos);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    return best ? { node: best.node, pos: this.worldOf(best, new THREE.Vector3()), dist: bestD } : null;
  }

  /** Live nodes within `maxR` (ground scan): id, kind, world pos, stock. */
  scan(pos: THREE.Vector3, maxR: number): { id: number; kind: string; pos: THREE.Vector3; left: number }[] {
    const out: { id: number; kind: string; pos: THREE.Vector3; left: number }[] = [];
    for (const [id, v] of this.vis) {
      if (v.left <= 0) continue;
      const w = this.worldOf(v, new THREE.Vector3());
      if (w.distanceTo(pos) <= maxR) out.push({ id, kind: v.node.kind, pos: w, left: v.left });
    }
    return out;
  }

  /** World position of a node (mining beam endpoint). */
  posOf(id: number): THREE.Vector3 | null {
    const v = this.vis.get(id);
    return v ? this.worldOf(v, new THREE.Vector3()) : null;
  }

  /** Gentle beacon pulse. */
  update(dt: number): void {
    this.t += dt;
    const pulse = 1 + Math.sin(this.t * 2.2) * 0.12;
    for (const v of this.vis.values()) {
      if (v.left > 0) v.glow.scale.set(14 * pulse, 14 * pulse, 1);
    }
  }

  /** Meshes are children of planet groups — planet disposal handles
   *  geometry; this just drops references. */
  dispose(): void {
    for (const v of this.vis.values()) {
      v.group.parent?.remove(v.group);
      v.group.traverse((o) => {
        if (o instanceof THREE.Mesh) o.geometry.dispose();
        if (o instanceof THREE.Sprite) o.material.dispose();
      });
    }
    this.vis.clear();
  }
}
